import {
    DEFAULT_CURRENCY,
    DEFAULT_PLACEHOLDER,
    LEDGER_TYPE_TONES,
    MoneyTones,
    TONE_CLASS_NAMES,
} from '../constants/money.constants';

/**
 * Money presentation as pure functions: how an amount is written out and what
 * colour it gets. Named exports so a view imports the one it needs without
 * instantiating a controller; `MoneyController` (core/) lends the same
 * functions to a controller's prototype for the pages that mix it in through
 * `Inherit`. One implementation, two ways in — the vocabulary itself lives in
 * `constants/money.constants`.
 *
 * `formatFullCurrency` in `utils/numbers` delegates here too, so the whole app
 * writes an exact amount one way: `Intl`, sign outside the symbol
 * (`-$1,234.56`), two decimals, never abbreviated.
 */

// Constructing an Intl.NumberFormat is the expensive part; the formatters are
// immutable, so one per currency is reused for the app's lifetime.
const exactFormatters = new Map();

/**
 * Amounts reach the client as numbers or as pg DECIMAL strings.
 * @param {unknown} value
 * @returns {number|null} null when the value is missing or unparseable
 */
export function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Formatter for an exact amount: symbol, thousands separators, two decimals.
 * Both fraction digits are pinned so a sub-dollar fee is never rounded away.
 * @param {string} [currency='USD'] ISO 4217 code
 * @returns {Intl.NumberFormat}
 */
export function exactMoneyFormatter(currency = DEFAULT_CURRENCY) {
    if (!exactFormatters.has(currency)) {
        exactFormatters.set(
            currency,
            new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }),
        );
    }
    return exactFormatters.get(currency);
}

/**
 * An exact amount: `$1,234.56`. Never rounded to whole dollars (fees are
 * routinely under a dollar) and never abbreviated — ops reads these figures to
 * reconcile real money.
 *
 * Only USD exists today, which is why it is the default, but the currency is
 * a parameter so a second one costs nothing later.
 *
 * @param {number|string|null|undefined} value
 * @param {string} [currency='USD'] ISO 4217 code
 * @param {string} [placeholder='—'] rendered for a missing or unparseable value
 * @returns {string}
 */
export function formatMoney(value, currency = DEFAULT_CURRENCY, placeholder = DEFAULT_PLACEHOLDER) {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return placeholder;
    return exactMoneyFormatter(currency).format(numeric);
}

/**
 * The class name that paints an amount. Callers pass this straight to a
 * `className` — they never pick a colour themselves.
 *
 * @param {string|null|undefined} tone a {@link MoneyTones} value
 * @returns {string} a class from `styles/money.module.scss`
 */
export function getMoneyColorClassName(tone) {
    return TONE_CLASS_NAMES[tone] || TONE_CLASS_NAMES[MoneyTones.NEUTRAL];
}

/**
 * Tone for a ledger row, by the direction its type moved the money — never
 * by whether it is owed. A settlement came back, so it is green; an advance
 * went out and a fee was charged, so both are descriptive.
 *
 * An advance row is deliberately NOT red. No ledger row carries a settled
 * marker, so red would paint an advance repaid three cycles ago exactly like
 * one taken yesterday, and would contradict a green "Paid" card sitting
 * directly above the same table. Colouring by the sign of the amount had the
 * mirror-image bug: advances arrive positive, so every one of them read green.
 * Whether the member owes anything is answered by the cards, from
 * `awaitingSettlementAmount` and `overdueAmount`.
 *
 * An unrecognised type is neutral rather than an error, so a new backend
 * value renders in a sane colour instead of breaking the row.
 *
 * Prefer {@link toneForLedgerRow} for anything rendered from an endpoint item:
 * it also knows about reconciliations.
 *
 * @param {string|null|undefined} type display type (`advance`, `settlement`, `service_fee`)
 * @returns {string} a {@link MoneyTones} value
 */
export function toneForLedgerType(type) {
    return LEDGER_TYPE_TONES[type] || MoneyTones.NEUTRAL;
}

/**
 * Tone for a consumer-ledger ITEM — {@link toneForLedgerType} plus the one
 * fact only the row carries: `is_reconciliation`. A settlement booked by the
 * admin reconcile action zeroes a balance; it is not money the member repaid,
 * so it must never read as green. The shared table also tags that row in
 * words, because colour is never the only signal.
 *
 * Reads `type` and falls back to `rawType`, so it accepts the consumer ledger
 * item and the support ticket's linked-ledger shape alike. A row without
 * `is_reconciliation` (the ticket's, today) gets the type rule only.
 *
 * @param {object|null|undefined} record a consumer-ledger item
 * @returns {string} a {@link MoneyTones} value
 */
export function toneForLedgerRow(record) {
    if (record?.is_reconciliation === true) return MoneyTones.NEUTRAL;
    return toneForLedgerType(record?.type ?? record?.rawType);
}
